import React, {useCallback, useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import classnames from "classnames";
import {useHistory} from "react-router-dom";
import CustomerAutocomplete from "../../text-fields/autocomplete/customer-autocomplete";

const AddQuoteDialog = ({open, setOpen, customer}) => {
    const history = useHistory();
    const [submitting, setSubmitting] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(customer);
    const isMounted = useIsMounted();

    /**
     * Sets the selected customer when the dialog is opened.
     */
    useEffect(() => {
        if (!open)
            return;
        setSelectedCustomer(customer ?? null);
        setSubmitting(false);
    }, [customer, open]);

    /**
     * Creates a quote for a selected customer by calling the api and if the result of the api is successful, then
     * navigates the operator to that orders page.
     * @param customer {any}
     */
    const createQuote = useCallback(async () => {
        const forApi = {
            customerId: selectedCustomer.id,
            parts: null,
        };
        setSubmitting(true);
        const response = await Api.createNewQuoteV2(forApi, true);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setOpen(false);
            history.push(routeFunctions.customer.sales.quotes.single(selectedCustomer.id, response.data?.internalId))
        }
    }, [selectedCustomer?.id, history, isMounted, setOpen])

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            className={'bound-dialog'}
        >
            <DialogTitle className={''} id="scroll-dialog-title">
                <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                    Create New Quote
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={classnames('px-3')}>
                <div className='my-3 w-100 d-flex flex-column'>
                    <p className='text-sm text-start text-color-1'>
                        Please Select the customer you would like to create this quote for:
                    </p>
                    {
                        !!customer && customer?.id === selectedCustomer?.id &&
                        <p className={'text-sm text-start text-color-1 mt-2'}>
                            We have automatically selected <span
                            className={'tertiary-main font-weight-semi-bold'}>{customer.name}</span> for you, but you can
                            still select another customer.
                        </p>
                    }

                    <div className={'mt-3'}/>
                    <CustomerAutocomplete
                        disabled={submitting}
                        value={selectedCustomer}
                        onChange={(_, customer) => setSelectedCustomer(customer)}
                        label={'Customer'}
                    />
                </div>
            </DialogContent>
            <DialogActions className={'d-flex flex-column px-0'}>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100 ml-0'>
                    <Button
                        disabled={!open || submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {submitting ? 'processing...' : 'Close'}
                    </Button>
                    <Col className='row no-gutters justify-content-end align-items-start w-100'>
                        <Button
                            disabled={!open || submitting || !selectedCustomer}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => createQuote()}
                            borderRadius={'0 0 10px 0'}
                        >
                            {submitting ? 'processing...' : 'Create Quote'}
                        </Button>
                    </Col>
                </Row>
            </DialogActions>
        </Dialog>
    );
}


export default AddQuoteDialog;
